import React from 'react';
import { ChevronRight, BookOpen, CheckCircle, ClipboardList } from 'lucide-react';
import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { useThemeColors } from '@/hooks/useThemeColors';
import { TopicData } from '@/types';

interface TopicListItemProps {
    topic: TopicData;
    courseSlug: string;
}

const TopicListItem: React.FC<TopicListItemProps> = ({ topic, courseSlug }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const isCompleted = topic.is_completed ?? false;
    const completedClasses = topic.completed_classes ?? 0;
    const hasAssignments = (topic.assignments?.length || 0) > 0;
    const isAssignmentOnlyTopic = hasAssignments && topic.classes_count === 0;

    return (
        <Link
            href={`/courses/${courseSlug}/topics/${topic.id}`}
            className={`group flex items-center justify-between py-3 px-4 rounded-lg transition-all duration-150 ${
                isAssignmentOnlyTopic
                    ? 'bg-amber-50 hover:bg-amber-100'
                    : isCompleted 
                    ? 'bg-green-50 hover:bg-green-100' 
                    : 'hover:bg-white'
            }`}
        >
            <div className="flex items-center space-x-3">
                {isAssignmentOnlyTopic ? (
                    <ClipboardList size={16} className="text-amber-600" />
                ) : isCompleted ? (
                    <CheckCircle size={16} className="text-green-500" />
                ) : (
                    <BookOpen size={16} className="text-gray-400" />
                )}
                <div>
                    <h4 className={`font-medium group-hover:${colors.iconColor} ${
                        isAssignmentOnlyTopic
                            ? 'text-amber-900'
                            : isCompleted
                            ? 'text-green-800'
                            : 'text-gray-900'
                    }`}>
                        {topic.title}
                    </h4>
                    <p className={`text-xs ${
                        isAssignmentOnlyTopic
                            ? 'text-amber-700'
                            : isCompleted
                            ? 'text-green-600'
                            : 'text-gray-500'
                    }`}>
                        {completedClasses}/{topic.classes_count} {t('course.classes', { count: topic.classes_count })}
                    </p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {isAssignmentOnlyTopic && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        {t('course.assignment')}
                    </span>
                )}
                {isCompleted && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        {t('course.completed')}
                    </span>
                )}
                <ChevronRight size={16} className={`transition-colors ${
                    isAssignmentOnlyTopic
                        ? 'text-amber-600'
                        : isCompleted 
                        ? 'text-green-500' 
                        : `text-gray-400 group-hover:${colors.iconColor}`
                }`} />
            </div>
        </Link>
    );
};

export default TopicListItem;
